import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  getCurrentUser,
  signOut as amplifySignOut,
  type AuthUser,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

/**
 * Auth status values. Mirror Amplify UI React's three-value union without
 * importing from `@aws-amplify/ui-react` (which would drag the UI library
 * back into the entry chunk graph and defeat the whole point of this
 * provider). Re-exported for consumers that need the type.
 */
export type AuthStatusValue = 'configuring' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  authStatus: AuthStatusValue;
  /** The current Cognito user, or undefined when unauthenticated/configuring. */
  user: AuthUser | undefined;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Provides authentication state derived from aws-amplify/auth and Hub events.
 *
 * Why this exists: `@aws-amplify/ui-react`'s `<Authenticator.Provider>` pulls
 * the entire UI library (forms, validators, theming — ~91 KiB compressed) into
 * the entry chunk. We only use it as a context wrapper here, not for any UI.
 * This thin Provider replicates the `authStatus / user / signOut` subset that
 * non-Authenticator-UI consumers access, so we can lazy-load the UI library
 * only on /sign-in and /admin/login.
 */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [authStatus, setAuthStatus] = useState<AuthStatusValue>('configuring');
  const [user, setUser] = useState<AuthUser | undefined>(undefined);

  const clearAuth = useCallback(() => {
    setUser(undefined);
    setAuthStatus('unauthenticated');
  }, []);

  const setAuthenticated = useCallback((u: AuthUser) => {
    setUser(u);
    setAuthStatus('authenticated');
  }, []);

  useEffect(() => {
    let cancelled = false;
    // `superseded` flips to true the first time Hub drives an auth-state
    // change. After that, the in-flight initial getCurrentUser result is
    // ignored — a Hub event (e.g. another tab signing in) reflects newer
    // reality than the initial fetch.
    let superseded = false;

    void getCurrentUser()
      .then((u) => {
        if (cancelled || superseded) return;
        setAuthenticated(u);
      })
      .catch(() => {
        if (cancelled || superseded) return;
        clearAuth();
      });

    const unlisten = Hub.listen('auth', (data) => {
      const event = (data as { payload?: { event?: string } })?.payload?.event;
      if (event === 'signedIn' || event === 'signInWithRedirect') {
        superseded = true;
        void getCurrentUser().then(setAuthenticated).catch(clearAuth);
      } else if (
        event === 'signedOut' ||
        event === 'tokenRefresh_failure' ||
        event === 'signInWithRedirect_failure'
      ) {
        // tokenRefresh_failure: Amplify's TokenOrchestrator clears tokens
        // on NotAuthorizedException (e.g. expired refresh token) and emits
        // ONLY tokenRefresh_failure — NOT signedOut. Without handling it
        // here, users with expired sessions would stay 'authenticated' in
        // our state while every API call fails silently.
        superseded = true;
        clearAuth();
      }
      // tokenRefresh (success) → no-op; session continues
    });

    return () => {
      cancelled = true;
      unlisten();
    };
  }, [clearAuth, setAuthenticated]);

  const signOut = useCallback(async () => {
    try {
      await amplifySignOut();
      // Hub 'signedOut' event handler above will update state on success.
    } catch (err) {
      // amplifySignOut() can reject (network error, stale token). The
      // user clicked sign-out; honor their intent locally even if Amplify
      // can't reach Cognito. Hub may or may not fire — be idempotent.
      clearAuth();
      throw err;
    }
  }, [clearAuth]);

  // Memoize the context value so consumers don't re-render on
  // every AuthProvider re-render. Without this, the object
  // identity churns each render and every useAuth() consumer
  // (RootLayout, CheckoutPage, etc.) re-renders — defeating
  // Amplify's useAuthenticator selector pattern's intent.
  const value = useMemo<AuthContextValue>(
    () => ({ authStatus, user, signOut }),
    [authStatus, user, signOut],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Read auth state. Throws if used outside a AuthProvider.
 *
 * Drop-in replacement for the subset of `@aws-amplify/ui-react`'s
 * `useAuthenticator((ctx) => [ctx.authStatus, ctx.user])` that the
 * codebase actually uses. Field shape matches Amplify's:
 *
 *   const { authStatus, user, signOut } = useAuth();
 *
 * No selector function. The Provider memoizes the context value via
 * useMemo so consumers only re-render when authStatus, user, or
 * signOut actually change. Without a selector, all three fields
 * trigger re-renders — that's fine since the Provider has no other
 * state and the consuming trees are bounded.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return ctx;
}
